import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {newRepoPath} from '@github-ui/paths'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ReposFilter} from '@github-ui/repos-filter'
import {Button, PageLayout} from '@primer/react'
import {useEffect, useState} from 'react'

import {OrgReposListContent} from '../components/OrgReposListContent'
import {getOrderedTypes, getTypeFromQuery, OrgSidebar, OrgSidebarCollapsedButton} from '../components/OrgSidebar'
import {useListResults} from '../hooks/use-list-results'
import type {PagePayload} from '../types'
import {orgReposPageRoute} from './org-repos-page-route'
import styles from './OrgReposPage.module.css'

export function OrgReposPageForDataRouter() {
  const {data} = useRouteQuery(orgReposPageRoute, 'mainQuery')
  const {org} = useRouteParams(orgReposPageRoute)

  return (
    <AnalyticsProvider appName="org-repos-page" category="Org Repos page" metadata={{page: 'org-repos'}}>
      <OrgReposPage payload={data} org={org} />
    </AnalyticsProvider>
  )
}

interface OrgReposPageProps {
  payload: PagePayload
  org?: string
}

export function OrgReposPage({payload, org}: OrgReposPageProps) {
  const {userInfo, definitions, typeFilters, searchable} = payload

  const {fetchedQuery, fetchResults} = useListResults()
  const [query, setQuery] = useState(fetchedQuery)

  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')
  const showFinderFilters = isFeatureEnabled('repos_allow_finder_filters_rollout')

  const orderedTypes = getOrderedTypes(typeFilters, showFinderFilters)
  const fetchedType = getTypeFromQuery(fetchedQuery, orderedTypes)

  useEffect(() => {
    setQuery(fetchedQuery)
  }, [fetchedQuery])

  const {canCreateRepository} = userInfo || {}

  function updateQuery(q: string) {
    setQuery(q)
  }

  function updateAndSubmitQuery(q: string) {
    updateQuery(q)
    fetchResults({q})
  }

  const listTitle = orderedTypes.find(({id}) => id === fetchedType)?.text || 'All'

  return (
    <PageLayout containerWidth="full" columnGap="none" padding="none">
      <PageLayout.Pane
        divider="line"
        hidden={{narrow: true, regular: false, wide: false}}
        padding="condensed"
        position="start"
      >
        <aside>
          <OrgSidebar type={fetchedType} onQueryChanged={updateAndSubmitQuery} types={orderedTypes} />
        </aside>
      </PageLayout.Pane>
      {/* setting px on top of the padding prop is required to visually compensate padding of the list items in the sidebar */}
      <PageLayout.Content padding="condensed" className={styles.orgReposPageContent}>
        {searchable && (
          <>
            <div className={styles.pageHeaderContainer}>
              <div className={styles.mobileNavContainer}>
                <OrgSidebarCollapsedButton
                  type={fetchedType}
                  onQueryChanged={updateAndSubmitQuery}
                  types={orderedTypes}
                />
              </div>
              <h1 className={styles.pageTitle}>{listTitle}</h1>
              <div className={styles.actionButtonsContainer}>
                {canCreateRepository && (
                  <Button as="a" href={newRepoPath({org})} variant="primary" size="medium">
                    New repository
                  </Button>
                )}
              </div>
            </div>
            <ReposFilter
              id="repos-list-filter"
              label="Search repositories"
              variant={showFilterDialog ? 'full' : 'input'}
              placeholder="Search repositories"
              filterValue={query}
              definitions={definitions}
              options={{
                withFinderTerms: showFinderFilters,
                withRelevanceSort: showFinderFilters,
              }}
              onChange={updateQuery}
              onSubmit={filterQuery => fetchResults({q: filterQuery.raw})}
            />
          </>
        )}

        <OrgReposListContent payload={payload} org={org} onQuerySubmit={updateAndSubmitQuery} />
      </PageLayout.Content>
    </PageLayout>
  )
}
